import { useEtablissement } from '@/contexts/EtablissementContext';
import type { EquipeStats } from '@/hooks/queries/useMembresQuery';
import { Button } from '@/components/ui/button';
import { Download, RefreshCw, ShieldCheck } from 'lucide-react';

interface ConformiteScoreProps {
  stats: EquipeStats;
  onRefresh: () => void;
  onExport: () => void;
  exportDisabled: boolean;
}

/**
 * Indicateur visuel de score de conformité
 * Affiche le % de l'équipe conforme avec le nom de l'établissement
 */
export const ConformiteScore = ({
  stats,
  onRefresh,
  onExport,
  exportDisabled,
}: ConformiteScoreProps) => {
  const { etablissement } = useEtablissement();
  const percentage = stats.total > 0 ? Math.round((stats.conformes / stats.total) * 100) : 0;
  
  // Empty state : gris neutre quand aucun membre
  const isEmpty = stats.total === 0;

  // Couleur dynamique selon le score (gris si empty)
  const getScoreTone = () => {
    if (isEmpty) {
      return {
        text: 'text-[hsl(var(--app-text-muted))]',
        chip:
          'border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-muted))] text-[hsl(var(--app-text-muted))]',
        progress: 'bg-[hsl(var(--app-border-subtle))]',
      };
    }
    if (percentage >= 80) {
      return {
        text: 'text-emerald-600',
        chip: 'border border-emerald-200/80 bg-emerald-50/80 text-emerald-700',
        progress: 'bg-emerald-500',
      };
    }
    if (percentage >= 50) {
      return {
        text: 'text-amber-600',
        chip: 'border border-amber-200/80 bg-amber-50/80 text-amber-700',
        progress: 'bg-amber-500',
      };
    }
    return {
      text: 'text-rose-600',
      chip: 'border border-rose-200/80 bg-rose-50/80 text-rose-700',
      progress: 'bg-rose-500',
    };
  };
  const scoreTone = getScoreTone();

  const now = new Date();
  const dateStr = now.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  const compactStats = [
    { label: 'Conformes', value: stats.conformes },
    { label: 'En attente', value: stats.en_attente + stats.verification_en_cours },
    { label: 'À régulariser', value: stats.a_regulariser },
  ];
  const ghostRaisedButtonClassName =
    'h-9 rounded-[14px] border border-[hsl(var(--app-border-subtle))] bg-white px-3.5 text-[12px] font-semibold text-[hsl(var(--app-text))] shadow-[0_8px_18px_rgba(15,23,42,0.06)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_14px_26px_rgba(15,23,42,0.08)]';

  return (
    <section className="app-surface rounded-[18px] p-3.5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <div className={`text-[28px] font-semibold leading-none tracking-[-0.04em] ${scoreTone.text}`}>
              {percentage}%
            </div>
            <div className={`rounded-[12px] px-2.5 py-1 text-[12px] font-medium ${scoreTone.chip}`}>
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" />
                Conforme
              </div>
            </div>
            {compactStats.map((item) => (
              <div
                key={item.label}
                className="rounded-[12px] border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-muted))] px-2.5 py-1 text-[12px] text-[hsl(var(--app-text))]"
              >
                <span className="font-medium text-[hsl(var(--app-text-strong))]">{item.value}</span> {item.label}
              </div>
            ))}
          </div>
          <p className="mt-2 text-[13px] leading-5 text-[hsl(var(--app-text-muted))]">
            {etablissement?.nom || 'Mon établissement'} · état au {dateStr}
          </p>
          {!isEmpty && (
            <p className="mt-1 text-[13px] leading-5 text-[hsl(var(--app-text-muted))]">
              {stats.conformes} sur {stats.total} membres sont actuellement conformes.
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 lg:shrink-0">
            <Button
              variant="outline"
              onClick={onRefresh}
              className={ghostRaisedButtonClassName}
            >
              <RefreshCw className="mr-2 h-3.5 w-3.5" />
              Mettre à jour
            </Button>
            <Button
              onClick={onExport}
              disabled={exportDisabled}
              className="h-9 px-3.5 text-[12px]"
            >
              <Download className="mr-2 h-3.5 w-3.5" />
              Exporter
            </Button>
        </div>
      </div>

      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-[hsl(var(--app-surface-muted))]">
        <div
          className={`h-full rounded-full transition-all ${scoreTone.progress}`}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {isEmpty && (
        <p className="mt-2.5 text-[12px] leading-5 text-[hsl(var(--app-text-muted))]">
          Ajoutez des membres à votre équipe pour commencer le suivi de conformité.
        </p>
      )}
    </section>
  );
};
